// Projectniveau (SPEC_workflow 8.4, SPEC_wijzigingen 2.5, 27okt 3, 4 en 6): alle calculaties van een
// P-nummer met versie, verzending en open posten, de twee percentages met hun referenties uit
// Bouwflow, labels, notities en de projectlog in één tijdlijn.
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '../components/AppShell';
import { BouwflowLink } from '../components/BouwflowLink';
import { Notities } from '../components/Notities';
import { ProjectLog } from '../components/ProjectLog';
import { huidigeVersieVerstuurd, ReferentieBlok, ToegerekendVanElders, type ProjectCalculatie } from '../components/ProjectNiveau';
import { ProjectTags } from '../components/Tags';
import { TweePercentages } from '../components/TweePercentages';
import { api } from '../lib/api';
import { fmtEUR, fmtDate, fmtDateTime, statusChipClass, statusLabel } from '../lib/format';

export function ProjectNiveauPagina() {
  const { sleutel } = useParams<{ sleutel: string }>();
  const q = useQuery({ queryKey: ['projectniveau', sleutel], queryFn: () => api.projects.projectniveau(sleutel!), enabled: !!sleutel });
  if (q.isLoading) return <AppShell><p className="text-secondary">Laden…</p></AppShell>;
  if (!q.data) return <AppShell><p>Project niet gevonden.</p></AppShell>;
  const { project: p, tags, notities } = q.data;
  const k = p.kost;
  const calculaties = p.calculaties as ProjectCalculatie[];
  const ref = p.referentie;
  const metPNummer = !!p.projectNummer && !p.sleutel.startsWith('calc:');
  const klanten = [...new Set(calculaties.map((c) => c.klantNaam).filter(Boolean))];
  const openTotaal = calculaties.reduce((s, c) => s + (c.openBedrag ?? 0), 0);

  return (
    <AppShell>
      <Link to="/" className="text-secondary text-sm font-medium hover:text-primary">← Terug</Link>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mt-2 mb-1">
        <h1 className="page-title">Project {p.projectNummer || '(zonder P-nummer)'}</h1>
        {metPNummer && (
          <BouwflowLink bouwflowProjectId={ref?.bouwflowProjectId} className="text-sm font-semibold text-accent-700">
            Open in Bouwflow
          </BouwflowLink>
        )}
      </div>
      <p className="text-secondary text-sm mb-3">
        {klanten.length > 0 && <>{klanten.join(', ')} · </>}
        Projectleider {ref?.projectleider ? <strong className="text-primary font-semibold">{ref.projectleider}</strong> : 'onbekend'}
      </p>
      <div className="mb-6">
        <ProjectTags sleutel={p.sleutel} tags={tags} />
      </div>

      <Notities sleutel={p.sleutel} notities={notities} />

      <div className="card mb-5">
        <h3 className="text-base font-semibold mb-3">Kost en percentages</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Werkposten + meerwerk" value={fmtEUR(k.werkpostenEnMeerwerk)} />
          <Stat label="Extra werken (telt mee)" value={fmtEUR(k.extraWerkMee)} />
          <Stat label="Kosten (telt mee)" value={k.aanrekeningMee ? '−' + fmtEUR(k.aanrekeningMee) : fmtEUR(0)} />
          <Stat label="Kost project" value={fmtEUR(k.kost)} sterk />
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          <TweePercentages data={p.percentages} detail />
        </div>
        <ReferentieBlok referentie={ref} />
      </div>

      <div className="card mb-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
          <h3 className="text-base font-semibold">Calculaties ({calculaties.length})</h3>
          {openTotaal > 0 && (
            <span className="text-xs text-secondary">
              Nog open voor oplevering: <strong className="text-accent-700 tabular-nums">{fmtEUR(openTotaal)}</strong>
            </span>
          )}
        </div>
        {calculaties.length === 0 ? <p className="text-sm text-secondary">Geen calculaties van dit project in de tool.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="thead-row">
                  <th className="text-left px-2 py-1">Onderaannemer</th>
                  <th className="text-left px-2 py-1">Status</th>
                  <th className="text-left px-2 py-1">Versie</th>
                  <th className="text-right px-2 py-1">Werkposten</th>
                  <th className="text-right px-2 py-1">Meerwerk</th>
                  <th className="text-right px-2 py-1">Extra werken</th>
                  <th className="text-right px-2 py-1">Kosten</th>
                  <th className="text-right px-2 py-1">Open</th>
                  <th className="text-right px-2 py-1">Te factureren</th>
                </tr>
              </thead>
              <tbody>
                {calculaties.map((c) => {
                  const verstuurd = huidigeVersieVerstuurd(c);
                  const versie = c.versie ?? 1;
                  const open = c.openBedrag ?? 0;
                  return (
                    <tr key={c.id} className="border-b border-lightgrey text-sm">
                      <td className="px-2 py-1.5"><Link to={`/project/${c.id}`} className="underline text-primary">{c.ploegTeam}</Link><div className="text-xs text-secondary whitespace-nowrap">{fmtDate(c.createdAt)}</div></td>
                      <td className="px-2 py-1.5 align-top"><span className={statusChipClass(c.status)}>{statusLabel(c.status)}</span></td>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <div className="font-semibold tabular-nums">v{versie}</div>
                        {verstuurd ? (
                          <div className="text-xs" title={c.verstuurdOp ? `Verstuurd op ${fmtDateTime(c.verstuurdOp)}` : undefined}>verstuurd: ja</div>
                        ) : (
                          <div className="text-xs text-secondary">
                            verstuurd: nee{c.verstuurdeVersie != null && c.verstuurdeVersie !== versie ? ` (v${c.verstuurdeVersie} wel)` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtEUR(c.subtotaal)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtEUR(c.meerwerk)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtEUR(c.extraWerk)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{c.aanrekening ? '−' + fmtEUR(c.aanrekening) : fmtEUR(0)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                        {open > 0
                          ? <span className="font-bold text-accent-700" title="Posten open voor oplevering: nog niet factureerbaar">{fmtEUR(open)}</span>
                          : <span className="text-secondary">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold whitespace-nowrap">{fmtEUR(c.totaal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <ToegerekendVanElders project={p} />
      </div>

      <ProjectLog sleutel={p.sleutel} />
    </AppShell>
  );
}

function Stat({ label, value, sterk = false }: { label: string; value: string; sterk?: boolean }) {
  return (
    <div>
      <div className="text-xs font-medium text-secondary">{label}</div>
      <div className={`text-lg tabular-nums ${sterk ? 'font-bold' : 'font-semibold'}`}>{value}</div>
    </div>
  );
}
